import type { WhatsAppTemplateParameterField } from "../types";

export type MediaHeaderFormat = "IMAGE" | "VIDEO" | "DOCUMENT";

export function isMediaHeader(
  value?: string | null,
): value is MediaHeaderFormat {
  return value === "IMAGE" || value === "DOCUMENT" || value === "VIDEO";
}

/**
 * A schema field that carries the per-send header media (image/video/document).
 * WhatsApp templates have at most one of these; body params stay text.
 */
export function isMediaHeaderField(field: WhatsAppTemplateParameterField) {
  return (
    field.component === "header" &&
    ["image", "video", "document"].includes(
      String(field.type ?? "").toLowerCase(),
    )
  );
}

/** `accept` attribute string for a native <input type="file">. */
export function mediaAccept(value?: string | null) {
  if (value === "IMAGE") return "image/jpeg,image/png";
  if (value === "VIDEO") return "video/mp4,video/3gpp";
  if (value === "DOCUMENT") {
    return "application/pdf,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document";
  }
  return undefined;
}

/** `accept` object for react-dropzone v14 (GIF excluded: transcode is off). */
export function dropzoneAccept(
  value?: string | null,
): Record<string, string[]> | undefined {
  if (value === "IMAGE") {
    return { "image/jpeg": [".jpg", ".jpeg"], "image/png": [".png"] };
  }
  if (value === "VIDEO") {
    return { "video/mp4": [".mp4"], "video/3gpp": [".3gp"] };
  }
  if (value === "DOCUMENT") {
    return {
      "application/pdf": [".pdf"],
      "text/plain": [".txt"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
    };
  }
  return undefined;
}

// WhatsApp Cloud API header-media size caps.
const MEDIA_SIZE_LIMIT_BYTES: Record<MediaHeaderFormat, number> = {
  IMAGE: 5 * 1024 * 1024,
  VIDEO: 16 * 1024 * 1024,
  DOCUMENT: 100 * 1024 * 1024,
};

export function mediaSizeLimitBytes(format?: string | null) {
  const key = String(format ?? "").toUpperCase();
  return isMediaHeader(key)
    ? MEDIA_SIZE_LIMIT_BYTES[key]
    : MEDIA_SIZE_LIMIT_BYTES.IMAGE;
}

export function formatBytes(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

export async function fileToBase64(file: File) {
  const buffer = await file.arrayBuffer();
  let binary = "";
  const bytes = new Uint8Array(buffer);
  for (let index = 0; index < bytes.length; index += 1) {
    binary += String.fromCharCode(bytes[index]);
  }
  return window.btoa(binary);
}
